import {
  cssVarName,
  roleColorValues,
  type RoleToken,
  type ThemeMode,
} from './roleTokenValues';

/**
 * Web override for the role tokens.
 *
 * Metro resolves `.web.ts` ahead of `.ts` for the web bundle, so `gripp-web`
 * gets CSS variables here while native keeps the static hex maps in
 * `roleTokens.ts`. That difference is the whole reason web can theme cheaply.
 *
 * Why this works: react-native-web's colour normaliser explicitly passes any
 * value starting with `var(` straight through to CSS
 * (`modules/isWebColor/index.js`). Verified against the compiler in this
 * workspace's version: `{ color: 'var(--text-primary)' }` emits
 * `.r-color-x{color:var(--text-primary);}`, whereas a plain hex is rewritten
 * to `rgba(...)`. So every one of the ~690 module-scope `StyleSheet.create`
 * calls themes without being touched: the class is static, the value behind
 * it is not.
 *
 * Native cannot do this (RN's `processColor` rejects `var()`), which is why
 * `gripp-app` needs a different mechanism and is out of scope for plan 0028.
 *
 * IMPORTANT: this file must only import from `roleTokenValues.ts`, never from
 * a module that has a `.web.ts` sibling. Metro platform-resolves *every*
 * relative import, including one that points back at this file's own base
 * name: the first cut of this feature lived in `colors.web.ts` and imported
 * `./colors`, which Metro resolved to `colors.web.ts` itself, and the
 * resulting self-import cycle threw "Cannot access 'roleColors' before
 * initialization" at bundle evaluation and took down the whole web app.
 * Jest does not apply `.web.ts` resolution, so only a browser catches this.
 *
 * The variables themselves are declared in `apps/gripp-web/public/index.html`
 * so they resolve before first paint. `themeCss.ts` generates that block and
 * `themeCss.spec.ts` fails if the two ever drift.
 */

const tokens = Object.keys(roleColorValues.light) as RoleToken[];

const varMap = tokens.reduce(
  (acc, token) => {
    acc[token] = `var(${cssVarName(token)})`;
    return acc;
  },
  {} as Record<RoleToken, string>,
);

/**
 * Both modes resolve to the same `var()` strings: the mode is chosen by the
 * `.dark` class on `<html>`, not by which object you read. The shape is kept
 * identical to the native export so consumers never branch on platform.
 */
export const roleColors: Record<ThemeMode, Record<RoleToken, string>> = {
  light: varMap,
  dark: varMap,
};

/** See `roleTokens.ts`: the `Colors.role` consumption surface. */
export const role = varMap;

export type { RoleToken, ThemeMode } from './roleTokenValues';
